import { CountUp } from '@/components/primitives/CountUp'
import { Card } from '@/components/ui/card'
import type { OverviewTile } from '@/lib/metrics'
import { cn } from '@/lib/utils'
import { Sparkline } from '@/pages/dashboard/components/Sparkline'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface Props {
  tile: OverviewTile
  index: number
  hint?: ReactNode
}

function formatValue(
  value: unknown,
  suffix?: string
): { display: string; numeric: number | null } {
  if (value === null || value === undefined)
    return { display: '—', numeric: null }
  if (typeof value === 'number') {
    const display =
      value % 1 === 0
        ? new Intl.NumberFormat().format(value)
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    return { display: suffix ? `${display}${suffix}` : display, numeric: value }
  }
  return { display: String(value), numeric: null }
}

export function KpiTile({ tile, index }: Props) {
  const navigate = useNavigate()
  const isCurrency =
    tile.key === 'gmv' || tile.key === 'ai_cost' || tile.key === 'wallets'
  const { display, numeric } = formatValue(
    tile.value,
    isCurrency ? '' : undefined
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, delay: Math.min(index, 12) * 0.04 }}
    >
      <Card
        role={tile.drill_to ? 'button' : undefined}
        tabIndex={tile.drill_to ? 0 : undefined}
        onClick={() => tile.drill_to && navigate(tile.drill_to)}
        onKeyDown={(e) => {
          if (tile.drill_to && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            navigate(tile.drill_to)
          }
        }}
        className={cn(
          'group flex h-32 flex-col gap-2 rounded-2xl p-4 transition-all',
          tile.drill_to &&
            'cursor-pointer hover:border-primary/30 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary'
        )}
      >
        <div className="flex items-start justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {tile.title}
          </p>
        </div>
        <div className="text-2xl font-semibold tabular-nums">
          {numeric !== null ? (
            <>
              {isCurrency ? '$' : null}
              <CountUp value={numeric} />
            </>
          ) : (
            display
          )}
        </div>
        {Array.isArray(tile.sparkline) && tile.sparkline.length > 0 && (
          <div className="mt-auto">
            <Sparkline data={tile.sparkline} />
          </div>
        )}
      </Card>
    </motion.div>
  )
}
